import { Field, Fieldset, Legend } from '@headlessui/react';
import { PlusCircleIcon, SparklesIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';
import { Button } from '../../../components/Button';
import { AddImage } from './AddImage';
import { Input, TextArea } from '../../../components/Input';
import { BookCreate } from '../api/types';

type AddBookFormProps = {
  isAiProcessing: boolean;
  setIsAiProcessing: (isAiProcessing: boolean) => void;
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>, type: 'back' | 'cover') => void;
  values: BookCreate;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onAddBook: () => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
};

export const AddBookForm = ({
  isAiProcessing,
  setIsAiProcessing,
  handleImageChange,
  values,
  onChange,
  onAddBook,
  onClose,
  isLoading
}: AddBookFormProps) => {
  return (
    <Fieldset className="space-y-2 rounded-xl bg-white/5">
      <Legend className="text-base/7 font-semibold text-black">Add new book</Legend>
      <div className="p-1 bg-cyan-800 flex items-center justify-between rounded-lg w-full">
        <div className="flex w-full items-center gap-2 text-center">
          <button
            onClick={() => setIsAiProcessing(true)}
            className={clsx(
              'w-1/2 p-1 rounded-lg text-sm font-semibold',
              isAiProcessing && 'bg-white',
              isAiProcessing ? 'text-cyan-800' : 'text-white'
            )}>
            AI-powered
          </button>
          <button
            onClick={() => setIsAiProcessing(false)}
            className={clsx(
              'w-1/2 p-1 rounded-lg text-sm font-semibold',
              !isAiProcessing && 'bg-white ',
              !isAiProcessing ? 'text-cyan-800' : 'text-white'
            )}>
            Manual input
          </button>
        </div>
      </div>
      {isAiProcessing ? (
        <AiProcessing
          handleImageChange={handleImageChange}
          cover={values.cover}
          back={values.back}
        />
      ) : (
        <ManualInput
          handleImageChange={handleImageChange}
          values={values}
          onFieldChange={onChange}
        />
      )}

      <div className="flex gap-3 mt-3">
        <Button onClick={onClose} variant="secondary" className="grow-2">
          Cancel
        </Button>
        <Button onClick={onAddBook} className="grow" isLoading={isLoading}>
          {isAiProcessing ? (
            <>
              <SparklesIcon className="size-4 fill-white" />
              Process with AI
            </>
          ) : (
            <>
              <PlusCircleIcon className="size-4 fill-white" />
              Add book
            </>
          )}
        </Button>
      </div>
    </Fieldset>
  );
};

type ManualInputProps = {
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'back') => void;
  values: BookCreate;
  onFieldChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
};

export const ManualInput = ({ handleImageChange, values, onFieldChange }: ManualInputProps) => {
  const fieldsCol1 = [
    {
      label: 'Title',
      name: 'title',
      onChange: onFieldChange,
      value: values.title,
      type: 'text',
      isRequired: true
    },
    {
      label: 'Author',
      name: 'author_name',
      value: values.author_name,
      onChange: onFieldChange,
      type: 'text',
      isRequired: true
    },
    {
      label: 'Category',
      name: 'category_name',
      value: values.category_name,
      onChange: onFieldChange,
      type: 'text',
      isRequired: true
    },
    {
      label: 'Price',
      name: 'price',
      value: values.price,
      onChange: onFieldChange,
      type: 'number',
      isRequired: true
    }
  ];

  const fieldsCol2 = [
    {
      label: 'Pages',
      name: 'pages',
      onChange: onFieldChange,
      value: values.pages,
      type: 'number',
      isRequired: false
    },
    {
      label: 'Year',
      name: 'year',
      onChange: onFieldChange,
      value: values.year,
      type: 'number'
    },
    {
      label: 'Language',
      value: values.language,
      onChange: onFieldChange,
      type: 'text'
    },
    {
      label: 'Publisher',
      name: 'publisher_name',
      value: values.publisher_name,
      onChange: onFieldChange,
      type: 'text'
    }
  ];
  return (
    <>
      <div className="flex gap-2">
        <Field className="w-1/2 h-full">
          <AddImage handleChange={(e) => handleImageChange(e, 'cover')} image={values.cover} />
        </Field>
        <Field className="w-1/2 h-full">
          <AddImage
            handleChange={(e) => handleImageChange(e, 'back')}
            image={values.back}
            imageType="back"
          />
        </Field>
      </div>
      <div className="flex gap-2">
        <div className="w-1/2 flex flex-col gap-2">
          {fieldsCol1.map((field) => (
            <Field key={field.name}>
              <Input
                value={field.value}
                type={field.type}
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                onChange={field.onChange}
                required={Boolean(field.isRequired)}
              />
            </Field>
          ))}
        </div>
        <div className="w-1/2 flex flex-col gap-2">
          {fieldsCol2.map((field) => (
            <Field key={field.name}>
              <Input
                value={field.value}
                type={field.type}
                name={field.name}
                label={field.label}
                isRequired={field.isRequired}
                onChange={field.onChange}
                required={Boolean(field.isRequired)}
              />
            </Field>
          ))}
        </div>
      </div>
      <div className="w-full">
        <TextArea
          label="Synopsis"
          value={values.synopsis}
          name="synopsis"
          onChange={onFieldChange}
        />
      </div>
    </>
  );
};

type AiProcessingProps = {
  handleImageChange: (event: React.ChangeEvent<HTMLInputElement>, type: 'cover' | 'back') => void;
  cover: File | null;
  back?: File | null;
};

export const AiProcessing = ({ handleImageChange, cover, back }: AiProcessingProps) => {
  return (
    <div className="flex gap-2 h-full">
      <Field className="w-1/2 min-h-[10rem]">
        <AddImage handleChange={(e) => handleImageChange(e, 'cover')} image={cover} />
      </Field>
      <Field className="w-1/2 min-h-[10rem]">
        <AddImage
          handleChange={(e) => handleImageChange(e, 'back')}
          image={back}
          imageType="back"
        />
      </Field>
    </div>
  );
};
